import { dateiAusgeben } from '../../../services/dateiAusgabe';
import type { GbuFassungInhalt, GbuUnterschrift, MassnahmeListenZeile } from '../../../services/sifa/gbuTypes';

/**
 * Ausgabe der GBU als PDF/Excel — der duenne Weg vom Knopf zur Datei.
 *
 * Beide Generatoren (services/sifa/gbuPdf.ts, gbuExcel.ts) werden erst beim
 * Klick geladen: jsPDF, autoTable, die eingebettete Schrift und exceljs
 * gehoeren nicht in den Start der App (Plan §7, O1). Heruntergeladen wird
 * ueber dateiAusgabe.ts — im Browser ein Download, in der Android-App der
 * Teilen-Dialog.
 */

export interface GbuAusgabeOpt {
  unterschriften?: GbuUnterschrift[];
  leereUnterschriftsfelder?: boolean;
  entwurf?: boolean;
}

/** Nur das PDF als Blob — fuer die Ablage nach der Freigabe. */
export const baueGbuPdf = async (inhalt: GbuFassungInhalt, opt: GbuAusgabeOpt): Promise<Blob> => {
  const { erzeugeGbuPdf } = await import('../../../services/sifa/gbuPdf');
  return erzeugeGbuPdf(inhalt, opt);
};

/** Dateiname wie in der Ablage („GBU-3-F2-Einschalen.pdf"), beim Entwurf mit Zusatz. */
const dateiname = async (inhalt: GbuFassungInhalt, endung: 'pdf' | 'xlsx', entwurf: boolean): Promise<string> => {
  const { gbuDateiname } = await import('../../../services/sifa/gbuPdf');
  const name = gbuDateiname(inhalt, endung);
  return entwurf ? name.replace(new RegExp(`\\.${endung}$`), `-Entwurf.${endung}`) : name;
};

/** Ein schon erzeugtes PDF (Vorschau) unter dem Ablage-Namen ausgeben — dieselbe Datei wie im Rahmen. */
export const gbuPdfBlobAusgeben = async (inhalt: GbuFassungInhalt, blob: Blob, entwurf: boolean): Promise<void> => {
  await dateiAusgeben(blob, await dateiname(inhalt, 'pdf', entwurf));
};

export const gbuPdfAusgeben = async (inhalt: GbuFassungInhalt, opt: GbuAusgabeOpt): Promise<void> => {
  const blob = await baueGbuPdf(inhalt, opt);
  await dateiAusgeben(blob, await dateiname(inhalt, 'pdf', !!opt.entwurf));
};

export const gbuExcelAusgeben = async (inhalt: GbuFassungInhalt, opt: GbuAusgabeOpt): Promise<void> => {
  const { erzeugeGbuExcel } = await import('../../../services/sifa/gbuExcel');
  const blob = await erzeugeGbuExcel(inhalt, opt);
  await dateiAusgeben(blob, await dateiname(inhalt, 'xlsx', !!opt.entwurf));
};

export interface MassnahmenAusgabeMeta {
  kundeName: string;
  bueroName?: string | null;
  /** Stichtag yyyy-mm-dd. */
  stand: string;
  untertitel?: string | null;
}

export const massnahmenlisteAusgeben = async (zeilen: MassnahmeListenZeile[], meta: MassnahmenAusgabeMeta, endung: 'pdf' | 'xlsx'): Promise<void> => {
  const pdf = await import('../../../services/sifa/gbuPdf');
  const name = pdf.massnahmenlisteDateiname(meta, endung);
  if (endung === 'pdf') {
    await dateiAusgeben(await pdf.erzeugeMassnahmenlistePdf(zeilen, meta), name);
    return;
  }
  const { erzeugeMassnahmenlisteExcel } = await import('../../../services/sifa/gbuExcel');
  await dateiAusgeben(await erzeugeMassnahmenlisteExcel(zeilen, meta), name);
};
